// src/pages/JudoCoachPro/components/JudoCoachProHero.jsx

import React, { useEffect, useState } from "react";
import {
  FaArrowRight,
  FaCheck,
  FaExternalLinkAlt,
} from "react-icons/fa";

import { JUDO_COACH_PRO, JUDO_COACH_HERO_POINTS } from "../data/judoCoachProData";

const cx = (...classes) => classes.filter(Boolean).join(" ");

export default function JudoCoachProHero() {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveImageIndex((currentIndex) => {
        return (currentIndex + 1) % JUDO_COACH_PRO.heroImages.length;
      });
    }, 6500);

    return () => window.clearInterval(intervalId);
  }, []);

  const handleDiscoverClick = () => {
    document.getElementById("jcp-features")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <section className="relative overflow-hidden bg-[#071a35] pt-[76px] text-white">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.16]"
        aria-hidden="true"
      >
        <div className="absolute -right-24 top-12 h-[420px] w-[420px] rounded-full border-[80px] border-[#0868f2]" />

        <div className="absolute -bottom-32 left-[30%] h-[360px] w-[360px] rounded-full border-[65px] border-[#ff7900]" />
      </div>

      <div className="relative mx-auto max-w-[1480px] px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center xl:gap-16">
          <div>
            <div className="flex items-center gap-4">
              <img
                src={JUDO_COACH_PRO.logo}
                alt="Logo Judo Coach Pro"
                className="h-16 w-16 object-contain sm:h-20 sm:w-20"
              />

              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-blue-200">
                  {JUDO_COACH_PRO.eyebrow}
                </p>

                <p className="mt-1 text-lg font-semibold text-white">
                  {JUDO_COACH_PRO.name}
                </p>
              </div>
            </div>

            <h1 className="mt-8 max-w-3xl text-4xl font-semibold leading-[1.05] tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl xl:text-7xl">
              {JUDO_COACH_PRO.title}

              <span className="block text-[#61a2ff]">
                {JUDO_COACH_PRO.highlightedTitle}
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">
              {JUDO_COACH_PRO.description}
            </p>

            <div className="mt-8 space-y-3">
              {JUDO_COACH_HERO_POINTS.map((point) => {
                const Icon = point.icon;

                return (
                  <div
                    key={point.label}
                    className="flex items-center gap-3 text-sm text-white/85"
                  >
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/10 text-[#ff7900]">
                      <Icon className="text-[11px]" />
                    </span>

                    <span>{point.label}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a
                href={JUDO_COACH_PRO.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cx(
                  "group inline-flex h-12 items-center justify-center gap-3 rounded-full",
                  "bg-white px-6 text-sm font-semibold text-[#071a35]",
                  "transition hover:bg-blue-50",
                )}
              >
                Accéder à l’application

                <span className="grid h-7 w-7 place-items-center rounded-full bg-[#ff7900] text-white">
                  <FaExternalLinkAlt className="text-[9px]" />
                </span>
              </a>

              <button
                type="button"
                onClick={handleDiscoverClick}
                className={cx(
                  "group inline-flex h-12 items-center justify-center gap-3 rounded-full",
                  "border border-white/25 bg-white/5 px-6",
                  "text-sm font-semibold text-white backdrop-blur-sm",
                  "transition hover:border-white/50 hover:bg-white/10",
                )}
              >
                Découvrir les fonctionnalités

                <FaArrowRight className="text-[10px] transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>

          <div className="relative">
            <div className="relative overflow-hidden rounded-[24px] border border-white/10 bg-white/5 p-2 shadow-[0_35px_100px_-45px_rgba(0,0,0,0.75)]">
              <div className="relative aspect-[16/11] overflow-hidden rounded-[18px] bg-slate-900">
                {JUDO_COACH_PRO.heroImages.map((image, index) => (
                  <img
                    key={image}
                    src={image}
                    alt={`Présentation de Judo Coach Pro ${index + 1}`}
                    className={cx(
                      "absolute inset-0 h-full w-full object-cover",
                      "transition-all duration-1000",
                      index === activeImageIndex
                        ? "scale-100 opacity-100"
                        : "scale-[1.025] opacity-0",
                    )}
                  />
                ))}

                <div className="absolute inset-0 bg-gradient-to-t from-[#071a35]/70 via-transparent to-transparent" />

                <div className="absolute bottom-5 left-5 right-5">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#071a35]/65 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-white backdrop-blur-md">
                    <FaCheck className="text-[#ff7900]" />
                    Application web installable
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-6 left-5 right-5 hidden grid-cols-2 gap-3 sm:grid">
              <div className="rounded-[16px] border border-white/10 bg-white px-5 py-4 text-[#071a35] shadow-xl">
                <p className="text-[9px] font-semibold uppercase tracking-[0.17em] text-[#0868f2]">
                  Accès judoka
                </p>

                <p className="mt-1 text-lg font-semibold">
                  Utilisation gratuite
                </p>
              </div>

              <div className="rounded-[16px] border border-white/10 bg-[#0868f2] px-5 py-4 text-white shadow-xl">
                <p className="text-[9px] font-semibold uppercase tracking-[0.17em] text-white/65">
                  Espace enseignant
                </p>

                <p className="mt-1 text-lg font-semibold">
                  Suivi des groupes
                </p>
              </div>
            </div>

            <div className="mt-5 flex justify-center gap-2 sm:mt-12">
              {JUDO_COACH_PRO.heroImages.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  onClick={() => setActiveImageIndex(index)}
                  className={cx(
                    "h-[3px] rounded-full transition-all",
                    index === activeImageIndex
                      ? "w-9 bg-[#ff7900]"
                      : "w-4 bg-white/25 hover:bg-white/50",
                  )}
                  aria-label={`Afficher le visuel ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}